import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { forgotPassword, resetPassword } from '../../../../app/store/slices/authSlice';
import { selectUser } from '../../../../app/store/slices/userSlice';

const ManagePasswordContent = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { status } = useSelector((state) => state.auth);
  
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [passwordStrength, setPasswordStrength] = useState('Weak');

  const checkPasswordStrength = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[\W_]/.test(password);
    const hasMinLength = password.length >= 8;

    if (hasUpperCase && hasNumber && hasSpecialChar && hasMinLength) {
      return 'Strong';
    }
    return 'Weak';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'newPassword') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.email && formData.email !== user.email) {
      toast.error('Please enter your registered email address');
      return;
    }

    try {
      await dispatch(forgotPassword(formData.email)).unwrap();
      toast.success('OTP sent to your email! Check your inbox.');
      setShowPasswordForm(true);
    } catch (error) {
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(error?.message || 'Failed to send OTP');
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordStrength !== 'Strong') {
      toast.error('Please provide a strong password');
      return;
    }

    try {
      const result = await dispatch(resetPassword({
        email: formData.email,
        otp: formData.otp,
        newPassword: formData.newPassword
      })).unwrap();

      // Handle API Response
      if (result.error) {
        switch (result.error.code) {
          case 'OTP_EXPIRED':
            toast.error('Verification code has expired. Please request a new one.');
            setShowPasswordForm(false);
            break;
          case 'INVALID_OTP':
            toast.error('Incorrect verification code');
            setFormData(prev => ({ ...prev, otp: '' }));
            break;
          case 'SAME_PASSWORD':
            toast.error('New password cannot be the same as your current password');
            setFormData(prev => ({
              ...prev,
              newPassword: '',
              confirmPassword: ''
            }));
            break;
          default:
            toast.error(result.error.details || 'Password reset failed');
        }
        return;
      }

      // Success case
      if (result.status === 200) {
        toast.success('Password updated successfully!');
        // Reset form and state
        setFormData({
          email: '',
          otp: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
        setPasswordStrength('Weak');
       
      }
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else if (err.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
        console.error('Reset password error:', err);
      }
    }
  };

  const renderPasswordForm = () => (
    <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-6">
      {/* Existing password form fields */}
      <div className="w-full">
        <label className="block text-sm sm:text-base text-gray-700 mb-1.5 sm:mb-2 font-medium">
          Enter OTP
        </label>
        <input
          type="text"
          name="otp"
          value={formData.otp}
          onChange={handleChange}
          className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent text-sm sm:text-base"
          required
          maxLength={6}
        />
      </div>

      {/* New Password field */}
      <div className="w-full">
        <label className="block text-sm sm:text-base text-gray-700 mb-1.5 sm:mb-2 font-medium">
          New Password
        </label>
        <div className="relative">
          <input
            type={showPassword.newPassword ? 'text' : 'password'}
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent text-sm sm:text-base"
            required
          />
          <button
            type="button"
            onClick={() => handleTogglePassword('newPassword')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword.newPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>

      {/* Password strength indicators */}
      {formData.newPassword && passwordStrength !== 'Strong' && (
        <div className="text-sm mt-2 bg-gray-50 p-2.5 sm:p-3 rounded-lg">
          <div className="space-y-2">
            <p className="text-gray-600 font-medium">Password must contain:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <span className={`flex items-center gap-1 ${!(/[A-Z]/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}`}>
                {!(/[A-Z]/.test(formData.newPassword)) ? '✗' : '✓'} One Uppercase Letter
              </span>
              <span className={`flex items-center gap-1 ${!(/\d/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}`}>
                {!(/\d/.test(formData.newPassword)) ? '✗' : '✓'} One Number
              </span>
              <span className={`flex items-center gap-1 ${!(/[\W_]/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}`}>
                {!(/[\W_]/.test(formData.newPassword)) ? '✗' : '✓'} One Special Character
              </span>
              <span className={`flex items-center gap-1 ${formData.newPassword.length < 8 ? 'text-red-500' : 'text-green-500'}`}>
                {formData.newPassword.length < 8 ? '✗' : '✓'} Minimum 8+ Characters
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Password field */}
      <div className="w-full">
        <label className="block text-sm sm:text-base text-gray-700 mb-1.5 sm:mb-2 font-medium">
          Confirm New Password
        </label>
        <div className="relative">
          <input
            type={showPassword.confirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent text-sm sm:text-base"
            required
          />
          <button
            type="button"
            onClick={() => handleTogglePassword('confirmPassword')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword.confirmPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
      </div>

      <div className="flex justify-center pt-2 sm:pt-4">
        <button
          type="submit"
          disabled={status === 'loading' || passwordStrength !== 'Strong'}
          className="w-full sm:w-auto min-w-[200px] px-4 sm:px-6 py-2.5 bg-btn-primary text-white rounded-lg hover:opacity-90 disabled:opacity-75 transition-all text-sm sm:text-base"
        >
          {status === 'loading' ? 'Updating...' : 'Change Password'}
        </button>
      </div>
    </form>
  );

  return (
    <div className="w-full max-w-2xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-semibold mb-6">Change Password</h2>
        
        {!showPasswordForm ? (
          // Email verification form
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verify Your Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-6 py-2.5 bg-btn-primary text-white rounded-lg hover:opacity-90 disabled:opacity-75 transition-all"
              >
                {status === 'loading' ? 'Sending...' : 'Send OTP'}
              </button>
            </div>
          </form>
        ) : (
          // Password change form
          <div className="w-full">
            {renderPasswordForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagePasswordContent;


